import axios from "axios";
import socialApiClient from "../../helpers/services/social-api-client";
import { Mentions } from "../interfaces/Mention";
import { PostVideo } from "../interfaces/postVideo";
import { blobFromDataURI } from "../../utils";

export type DirectVideoSocialProvider =
  | "instagramLogin"
  | "instagram"
  | "facebook"
  | "twitter"
  | "tiktok"
  | "youtube"
  | "linkedInBusiness";

type InitResponse = {
  message?: string;
  payload: {
    socialPost: Record<string, unknown>;
    upload: {
      url: string;
      key: string;
      bucket: string;
      publicUrl: string;
      headers: Record<string, string>;
      expiresInSeconds: number;
    };
  };
};

type CompleteResponse = {
  message?: string;
  payload: {
    socialPost: Record<string, unknown>;
    media: unknown;
    alreadyCompleted?: boolean;
  };
};

export type SocialVideoUploadProgress = {
  phase: "init" | "uploading" | "completing" | "done" | "error";
  percent?: number;
  socialPostId?: string | number;
};

function resolveVideoBlob(video: PostVideo): Blob {
  if (video.file) return video.file;
  if (video.data) return blobFromDataURI(video.data);
  throw new Error("Video file is missing");
}

export async function socialPostVideoDirect(options: {
  companyId: string;
  socialProvider: DirectVideoSocialProvider;
  text: string;
  mentions: Mentions[];
  video: PostVideo;
  scheduleDate?: string;
  repostId?: string;
  tiktokDirectPostPayload?: unknown;
  youtubeDirectPostPayload?: unknown;
  onProgress?: (progress: SocialVideoUploadProgress) => void;
  onInitialized?: (payload: InitResponse["payload"]) => void;
}): Promise<CompleteResponse> {
  const {
    companyId,
    socialProvider,
    text,
    mentions,
    video,
    scheduleDate,
    repostId,
    tiktokDirectPostPayload,
    youtubeDirectPostPayload,
    onProgress,
    onInitialized,
  } = options;

  const blob = resolveVideoBlob(video);
  const contentType = video.type || blob.type || "video/mp4";
  const fileName = video.name || `video-${Date.now()}.mp4`;
  const fileSize = video.size || blob.size;

  onProgress?.({ phase: "init", percent: 0 });

  const { data: initData } = await socialApiClient().post<InitResponse>(
    `social/posts/video/init`,
    {
      socialProvider,
      text,
      mentions,
      scheduleDate: scheduleDate || undefined,
      repostId: repostId || undefined,
      fileName,
      contentType,
      fileSize,
      tiktokDirectPostPayload,
      youtubeDirectPostPayload,
    },
    {
      params: { companyId },
      timeout: 120000,
    }
  );

  const initPayload = initData.payload;
  const socialPostId = (initPayload.socialPost as { id?: string | number })?.id;
  onInitialized?.(initPayload);
  onProgress?.({
    phase: "uploading",
    percent: 0,
    socialPostId,
  });

  try {
    await axios.put(initPayload.upload.url, blob, {
      headers: {
        ...initPayload.upload.headers,
        "Content-Type": contentType,
      },
      timeout: 60 * 60 * 1000,
      maxBodyLength: Infinity,
      maxContentLength: Infinity,
      onUploadProgress: (event) => {
        if (!event.total) return;
        const percent = Math.min(
          99,
          Math.round((event.loaded / event.total) * 100)
        );
        onProgress?.({
          phase: "uploading",
          percent,
          socialPostId,
        });
      },
    });
  } catch (error) {
    try {
      await socialApiClient().post(
        `social/posts/video/${socialPostId}/fail`,
        {
          errorMessage:
            (error as any)?.message || "Error uploading video to storage",
        },
        { params: { companyId } }
      );
    } catch (_) {
      /* ignore fail-mark errors */
    }
    onProgress?.({ phase: "error", socialPostId });
    throw error;
  }

  onProgress?.({
    phase: "completing",
    percent: 100,
    socialPostId,
  });

  const { data: completeData } = await socialApiClient().post<CompleteResponse>(
    `social/posts/video/${socialPostId}/complete`,
    { key: initPayload.upload.key },
    {
      params: { companyId },
      timeout: 120000,
    }
  );

  onProgress?.({
    phase: "done",
    percent: 100,
    socialPostId,
  });

  return completeData;
}
